import React, { useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import PlacesAutocomplete from 'react-places-autocomplete';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { FONT_ENUM } from '../../types/global/fonts.types';

export interface ResidentialAddressFieldProps {
    value: string;
    onChange: (address: string) => void;
    fieldSx?: Record<string, unknown>;
    highlightColor: string;
    labelColor: string;
    mutedColor: string;
    surfaceColor: string;
    hoverBgColor: string;
    textColor: string;
}

/**
 * Soft Google Places autocomplete for residential address, with manual entry fallback.
 */
export function ResidentialAddressField({
    value,
    onChange,
    fieldSx,
    highlightColor,
    labelColor,
    mutedColor,
    surfaceColor,
    hoverBgColor,
    textColor,
}: ResidentialAddressFieldProps) {
    const { isLoaded: isMapsLoaded } = useGoogleMapsLoader();
    const [manualEntry, setManualEntry] = useState(false);
    const [searchInput, setSearchInput] = useState(value || '');

    return (
        <Box sx={{ mt: 1 }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={manualEntry}
                        onChange={(_, checked) => {
                            setManualEntry(checked);
                            if (checked) {
                                setSearchInput('');
                            } else {
                                setSearchInput(value || '');
                            }
                        }}
                        sx={{
                            color: highlightColor,
                            '&.Mui-checked': { color: highlightColor },
                        }}
                    />
                }
                label={
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: mutedColor,
                            fontSize: 14,
                        }}>
                        Type residential address manually (instead of Google search)
                    </Typography>
                }
                sx={{ mb: 1, alignItems: 'center' }}
            />
            {!manualEntry && isMapsLoaded && (
                <Box sx={{ position: 'relative', mb: 2 }}>
                    <PlacesAutocomplete
                        value={searchInput}
                        onChange={setSearchInput}
                        onSelect={address => {
                            onChange(address);
                            setSearchInput(address);
                        }}
                        searchOptions={{ types: ['address'] }}>
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                            <div style={{ position: 'relative' }}>
                                <TextField
                                    fullWidth
                                    label="Search residential address"
                                    {...getInputProps({
                                        placeholder: 'Start typing your residential address…',
                                    })}
                                    sx={fieldSx}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        zIndex: 20,
                                        left: 0,
                                        right: 0,
                                        backgroundColor: surfaceColor,
                                        boxShadow: '0px 4px 12px rgba(0,0,0,0.35)',
                                        borderRadius: 8,
                                        marginTop: 4,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                    }}>
                                    {loading && (
                                        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                                            <CircularProgress size={22} />
                                        </Box>
                                    )}
                                    {suggestions.map(suggestion => (
                                        <div
                                            {...getSuggestionItemProps(suggestion)}
                                            key={suggestion.placeId}
                                            style={{
                                                padding: '10px 12px',
                                                cursor: 'pointer',
                                                color: textColor,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: 14,
                                                backgroundColor: suggestion.active ? hoverBgColor : 'transparent',
                                            }}>
                                            {suggestion.description}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </PlacesAutocomplete>
                    {value ? (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 1,
                                color: mutedColor,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Selected: {value}
                        </Typography>
                    ) : null}
                </Box>
            )}
            {!manualEntry && !isMapsLoaded && (
                <Typography variant="caption" sx={{ color: mutedColor, mb: 1, display: 'block' }}>
                    Loading address search… or use manual entry above.
                </Typography>
            )}
            {manualEntry && (
                <TextField
                    label="Residential address"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Your full residential address"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    sx={fieldSx}
                />
            )}
            <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: labelColor, fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                Required for bank payout accounts. Use your residential address, not the bank branch.
            </Typography>
        </Box>
    );
}

export default ResidentialAddressField;
